import { memo, useRef, useState } from 'react';

import { useAriaId } from '@components/component-helpers';
import { UploadedFile } from '@components/file-field';
import { FileAcceptText } from '@components/file-field/file-accept-text';
import {
  FileInput,
  FileObject,
  FileResponseObject,
  transformFileObject,
} from '@components/file-field/file-input';
import { FileInputContent } from '@components/file-field/file-input-content';
import { FileFieldBaseProps } from '@components/file-field/types';
import { FormError } from '@components/form-error';
import { FormLabelContainer } from '@components/form-label/form-label';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

export type InstantUploadMultiFileFieldProps = FileFieldBaseProps & {
  defaultValue?: Array<FileResponseObject>;
  onSelect(file: File): Promise<FileResponseObject>;
  onChange(files: Array<FileObject> | null): void;
};

export const InstantUploadMultiFileField = memo<InstantUploadMultiFileFieldProps>(
  ({
    label,
    hideLabel,
    accept,
    fileType,
    error,
    placeholder: userPlaceholder,
    onChange,
    onSelect,
    defaultValue = [],
    isDisabled,
  }) => {
    const { t } = useScopedTranslate(locale);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputId = useAriaId('files');
    const [files, setFiles] = useState<Array<FileObject>>(defaultValue.map(transformFileObject));
    const showFilePreviewModal = usePreviewFileModal();
    const getFileSrc = (file: FileObject) => file.fullUrl ?? file.url;
    const previewFiles = files.map((file) => ({ name: file.name, src: getFileSrc(file) }));

    const placeholder = userPlaceholder ?? t('placeholder');

    return (
      <Stack space={1}>
        <FormLabelContainer id={inputId} label={label} hideLabel={hideLabel} />
        <FileInput
          isDisabled={isDisabled}
          id={inputId}
          ref={fileInputRef}
          accept={accept}
          fileType={fileType}
          isMulti
          onChange={async (selectedFiles) => {
            const data = (await Promise.all(selectedFiles.map(onSelect))) as Array<FileObject>;
            const newFiles = data.map(transformFileObject);
            const finalFiles = [...files, ...newFiles];
            onChange(finalFiles);
            setFiles(finalFiles);
          }}
        />
        {files.map((file) => (
          <UploadedFile
            key={file.url}
            isDisabled={isDisabled}
            name={file.name}
            onPreview={() =>
              showFilePreviewModal({
                formFile: { name: file.name, src: getFileSrc(file) },
                filesList: previewFiles,
              })
            }
            onRemove={() => {
              const finalFiles = files.filter((currentFile) => currentFile.url !== file.url);
              setFiles(finalFiles);
              onChange(finalFiles);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
          />
        ))}
        <FileInputContent
          isDisabled={isDisabled}
          error={error}
          placeholder={placeholder}
          onClick={() => fileInputRef.current?.click()}
        />

        <FileAcceptText accept={accept} />
        <FormError text={error} />
      </Stack>
    );
  }
);

const locale = {
  en: {
    placeholder: 'Upload files',
    accepted: 'Accepted file types:',
  },
  is: {
    placeholder: 'Bæta við skrám',
    accepted: 'Leyfðar skráartegundir:',
  },
  dk: {
    placeholder: 'Vælg filer',
    accepted: 'Accepterede filtyper:',
  },
};
